// Client sign-in. Split screen: firm-branded gradient on the left, form on
// the right. No AI language anywhere. The magic-link path is a light stub
// today: the back end only supports password auth for opt-in clients, so the
// magic-link tab surfaces instructions to open the link the firm sent instead
// of trying to mint a new one (which would require a manager endpoint).
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { KeyRound, Mail } from 'lucide-react';
import { clientAuthApi } from '../../api/public';
import { useAuth } from '../../context/AuthContext';
import { useFirmBranding } from '../../hooks/useFirmBranding';
import { cn } from '../../lib/cn';
import { Button } from '../../ui/Button';
import FirmHeader from '../../components/client/FirmHeader';
import GradientBackdrop from '../../components/client/GradientBackdrop';

export default function ClientLogin() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const branding = useFirmBranding(null); // default BridgeIntoTax brand
  const [mode, setMode] = useState('password'); // password | magic
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [magicMsg, setMagicMsg] = useState('');

  const submitPassword = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const { access_token } = await clientAuthApi.login(email, password);
      localStorage.setItem('bit_token', access_token);
      await refresh();
      nav('/client/home');
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Those credentials did not match.');
    } finally {
      setBusy(false);
    }
  };

  const submitMagic = (e) => {
    e.preventDefault();
    setErr('');
    setMagicMsg(
      'Check your inbox for the most recent upload link from your preparer. It signs you in without a password.',
    );
  };

  return (
    <div
      className="relative flex min-h-screen bg-[hsl(var(--bg))] text-[hsl(var(--fg))]"
      style={branding.cssVars}
    >
      <aside className="relative hidden flex-col items-center justify-center overflow-hidden px-10 md:flex md:w-2/5 lg:w-[40%]">
        <GradientBackdrop />
        <div className="relative z-10 flex max-w-sm flex-col items-center gap-6 text-center">
          <FirmHeader branding={branding} />
          <p className="text-sm text-[hsl(var(--fg-muted))]">
            Secure tax document portal. Your files stay private to you and your preparer.
          </p>
        </div>
      </aside>

      <main className="relative flex w-full flex-col justify-center px-6 py-12 md:w-3/5 md:px-16 lg:w-[60%]">
        <div className="mx-auto w-full max-w-md">
          <div className="md:hidden">
            <FirmHeader branding={branding} />
          </div>
          <h1 className="mt-8 text-3xl font-semibold tracking-tight md:mt-0">Welcome back.</h1>
          <p className="mt-2 text-sm text-[hsl(var(--fg-muted))]">
            Sign in to check on your return or add more documents.
          </p>

          <div className="mt-8 inline-flex rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg-raised))] p-1">
            <TabBtn active={mode === 'password'} onClick={() => { setMode('password'); setErr(''); setMagicMsg(''); }}>
              <KeyRound size={14} /> Password
            </TabBtn>
            <TabBtn active={mode === 'magic'} onClick={() => { setMode('magic'); setErr(''); setMagicMsg(''); }}>
              <Mail size={14} /> Magic link
            </TabBtn>
          </div>

          <AnimatePresence mode="wait">
            {mode === 'password' ? (
              <motion.form
                key="pw"
                onSubmit={submitPassword}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2 }}
                className="mt-6 flex flex-col gap-4"
              >
                <Field label="Email" type="email" value={email} setValue={setEmail} autoFocus />
                <Field label="Password" type="password" value={password} setValue={setPassword} />
                {err && <div className="text-sm text-[hsl(var(--danger))]">{err}</div>}
                <Button type="submit" size="lg" disabled={busy || !email || !password}>
                  {busy ? 'Signing in...' : 'Sign in'}
                </Button>
                <div className="text-xs text-[hsl(var(--fg-muted))]">
                  First time here? Use the upload link your preparer sent and opt in when asked.
                </div>
              </motion.form>
            ) : (
              <motion.form
                key="mg"
                onSubmit={submitMagic}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2 }}
                className="mt-6 flex flex-col gap-4"
              >
                <Field label="Email" type="email" value={email} setValue={setEmail} autoFocus />
                {magicMsg ? (
                  <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg-raised))] p-4 text-sm text-[hsl(var(--fg-muted))]">
                    {magicMsg}
                  </div>
                ) : (
                  <Button type="submit" size="lg">
                    Send me the link
                  </Button>
                )}
                <div className="text-xs text-[hsl(var(--fg-muted))]">
                  We'll email you the most recent upload link your preparer created. It signs you in without a password.
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>

        <Link
          to="/login"
          className="absolute bottom-4 right-5 text-[12px] font-medium uppercase tracking-wider text-[hsl(var(--fg-muted))] transition-colors hover:text-[hsl(var(--fg))]"
        >
          Tax professional sign in
        </Link>
      </main>
    </div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors duration-120',
        active
          ? 'bg-[hsl(var(--card))] text-[hsl(var(--fg))] shadow-elev-1'
          : 'text-[hsl(var(--fg-muted))] hover:text-[hsl(var(--fg))]',
      )}
    >
      {children}
    </button>
  );
}

function Field({ label, type = 'text', value, setValue, autoFocus = false }) {
  return (
    <label className="block text-xs font-medium text-[hsl(var(--fg-muted))]">
      {label}
      <input
        type={type}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        required
        autoFocus={autoFocus}
        className="mt-2 block w-full rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg-raised))] px-3 py-2.5 text-sm text-[hsl(var(--fg))] outline-none focus:border-[hsl(var(--primary))] focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)]"
      />
    </label>
  );
}
